<!-- app-subdomain-creators/src/Components/CreatorChatVideoMemoRecorder.vue -->

<template>
  <transition name="fade" mode="out-in">
    <div
      v-if="show"
      class="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 p-2 sm:p-10"
    >
      <!-- Modal window -->
      <div
        class="p-2 sm:p-6 rounded-3xl shadow-2xl w-full h-full backdrop-blur-md relative flex items-center justify-center overflow-hidden"
        style="background-color: rgba(255, 255, 255, 0.459)"
        @click.stop
      >
        <!-- Close button -->
        <button
          @click="handleClose"
          class="px-2 py-1 rounded-md absolute top-4 right-4 text-3xl font-bold leading-none hover:bg-black hover:bg-opacity-65 text-zinc-200 z-10"
        >
          &times;
        </button>

        <div
          class="bg-zinc-700 rounded-3xl shadow-lg px-2 py-4 sm:p-10 w-full md:w-[800px] mx-auto flex flex-col justify-center space-y-4 border border-zinc-400"
        >
          <h2
            class="text-zinc-200 pl-2 sm:pl-0 text-left text-xl sm:text-2xl font-semibold items-start"
          >
            Record Video Memo
          </h2>
          <div class="w-full bg-zinc-600 h-[1px] my-10 text-center"></div>

          <!-- Video / Avatar area -->
          <div class="relative">
            <div class="aspect-w-16 aspect-h-9 w-full relative">
              <video
                ref="videoElement"
                class="rounded-3xl bg-zinc-600 object-cover w-full h-full"
                autoplay
                playsinline
                :muted="videoMuted"
              >
                Your browser does not support the video tag.
              </video>

              <!-- Placeholder avatar when no stream and no recording -->
              <div
                v-if="!hasStream && !isRecording && !hasRecording"
                class="absolute inset-0 flex items-center justify-center bg-zinc-800 rounded-3xl"
              >
                <div class="flex flex-col items-center gap-3">
                  <div
                    v-if="avatarUrl"
                    class="w-32 h-32 rounded-full overflow-hidden"
                  >
                    <img
                      :src="avatarUrl"
                      alt="Creator avatar"
                      class="w-full h-full object-cover"
                    />
                  </div>
                  <div
                    v-else
                    class="w-20 h-20 rounded-full bg-zinc-600 flex items-center justify-center text-3xl text-zinc-300"
                  >
                    📹
                  </div>

                  <p class="text-zinc-300 text-sm px-4 text-center leading-relaxed">
                    <template v-if="cameraError">
                      <span class="block text-lg">
                        No camera or microphone detected, or permission is blocked.
                      </span>
                      <span class="block mt-1">
                        Please allow access in your browser settings to record a video memo.
                      </span>
                    </template>

                    <template v-else>
                      <span class="block text-lg">
                        Checking for and loading your camera and microphone…
                      </span>
                      <span class="block mt-1">
                        If prompted, please grant permission so you can start recording.
                      </span>
                    </template>
                  </p>
                </div>
              </div>
            </div>

        <!-- Recording indicator + countdown -->
        <div class="absolute top-4 right-4 flex items-start gap-3">
        <!-- Countdown warning (shows after 40s) -->
        <div
            v-if="isRecording && showLimitWarning"
            class="max-w-[350px] bg-black/30 backdrop-blur px-3 py-2 rounded-xl border border-white/10 text-left"
        >
            <div class="text-[12px] leading-snug text-zinc-100">
            Voice Messages Limit is 1 Min. You have:
            <span class="font-semibold tabular-nums">{{ formattedTimeRemaining }}</span>
            remaining before message ends.
            </div>
        </div>

        <!-- Red ping -->
        <div class="relative flex items-center justify-center mt-1">
            <span
            v-if="isRecording"
            class="animate-ping absolute h-6 w-6 rounded-full bg-red-400 opacity-75"
            ></span>
            <span
            v-if="isRecording"
            class="relative inline-flex rounded-full h-6 w-6 bg-red-500"
            ></span>
        </div>
        </div>


        <!-- Lower Right Hand Corner Timer -->
            <div v-if="isRecording && showLimitWarning" class="absolute bottom-4 right-8 flex items-start gap-3">
            <span class="font-semibold text-[#2d8bffce] text-[40px]">{{ formattedTimeRemaining }}</span>
            </div>


          </div>

          <div class="w-full bg-zinc-600 h-[1px] my-4"></div>

          <!-- Action buttons -->
          <div class="text-end">
            <!-- Test Camera Button (only if auto-start failed / error) -->
            <button
              v-if="cameraError && !hasStream && !isRecording && !hasRecording"
              class="bg-zinc-600 hover:bg-blue-600 text-zinc-200 py-2 px-4 rounded-md transition duration-200 font-semibold"
              @click="startCamera"
            >
              Test Camera
            </button>

            <!-- Start Recording Button -->
            <button
              v-if="hasStream && !isRecording && !hasRecording"
              class="bg-zinc-600 hover:bg-green-500 text-zinc-200 py-2 px-4 rounded-md transition duration-200 font-semibold"
              @click="startRecording"
            >
              Start Recording
            </button>

            <!-- Stop Recording Button -->
            <div v-if="isRecording" class="items-end">
              <button
                class="bg-zinc-600 font-semibold hover:bg-red-500 text-zinc-200 py-2 px-4 rounded-md transition duration-200"
                @click="stopRecording"
              >
                Stop Recording
              </button>
              <div class="text-red-400 py-2 font-semibold">
                Recording in progress...
              </div>
            </div>

            <!-- Play, Start Over, and Send Buttons -->
            <div
              class="flex flex-col sm:flex-row justify-between w-full mx-auto gap-2"
              v-if="hasRecording && !isRecording"
            >
              <button
                class="bg-zinc-600 hover:bg-orange-500 text-zinc-200 py-2 px-4 rounded-md transition duration-200 font-semibold"
                @click="startOver"

              >
                Start Over
              </button>

              <button
                class="bg-zinc-600 font-semibold hover:bg-yellow-500 text-zinc-200 py-2 px-4 rounded-md transition duration-200"
                @click="playRecordedMessage"
              >
                Play Recorded Message
              </button>

              <!-- 🔥 UPDATED: Send button with spinner -->
              <button
                class="bg-zinc-600 font-semibold hover:bg-teal-500 text-white py-2 px-4 rounded-md transition duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                @click="sendToChat"
                :disabled="isUploading || !hasRecording"
              >
                <!-- Normal label -->
                <span v-if="!isUploading">
                  Send to Chat
                </span>

                <!-- Loading state -->
                <span v-else class="flex items-center gap-2">
                  <span class="relative flex items-center justify-center">
                    <!-- outer ring -->
                    <span class="absolute inset-0 rounded-full border-4 border-[#6831ff33]"></span>
                    <!-- spinning ring -->
                    <span class="size-5 animate-spin rounded-full border-4 border-[#6831ff] border-t-transparent"></span>
                  </span>
                  <span>Sending…</span>
                </span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </transition>
</template>

<script setup>
import {
  ref,
  computed,
  watch,
  onBeforeUnmount,
} from 'vue';

const props = defineProps({
  show: {
    type: Boolean,
    default: false,
  },
  uploadChatVideoFn: {
    type: Function,
    required: true,
  },
  avatarUrl: {
    type: String,
    default: '',
  },
});

const emit = defineEmits(['close', 'send']);

const videoElement = ref(null);
const videoStream = ref(null);
const videoMuted = ref(true);
const recordedChunks = ref([]);
const recordedBlobUrl = ref(null);
const isRecording = ref(false);
const isUploading = ref(false);
const cameraError = ref(null);

let mediaRecorder = null;

const hasStream = computed(() => !!videoStream.value);
const hasRecording = computed(() => recordedChunks.value.length > 0);

// 60s hard cap
const MAX_RECORDING_SECONDS = 60;
// start warning at 40s elapsed => 20s remaining
const WARNING_START_AT_SECONDS = 40;

const recordingSeconds = ref(0);
let recordingInterval = null;
let recordingAutoStopTimeout = null;

const showLimitWarning = computed(() => recordingSeconds.value >= WARNING_START_AT_SECONDS);

const timeRemainingSeconds = computed(() => {
  const remaining = MAX_RECORDING_SECONDS - recordingSeconds.value;
  return remaining > 0 ? remaining : 0;
});

const formattedTimeRemaining = computed(() => {
  const s = timeRemainingSeconds.value;
  const mm = String(Math.floor(s / 60)).padStart(2, '0');
  const ss = String(s % 60).padStart(2, '0');
  return `${mm}:${ss}`;
});

const clearRecordingTimers = () => {
  if (recordingInterval) {
    clearInterval(recordingInterval);
    recordingInterval = null;
  }
  if (recordingAutoStopTimeout) {
    clearTimeout(recordingAutoStopTimeout);
    recordingAutoStopTimeout = null;
  }
};


const createPreviewFromChunks = () => {
  if (!recordedChunks.value.length || !videoElement.value) return;

  if (recordedBlobUrl.value) {
    try {
      URL.revokeObjectURL(recordedBlobUrl.value);
    } catch (_) {
        // nothing
    }
  }

  const blob = new Blob(recordedChunks.value, { type: 'video/webm' });
  const url = URL.createObjectURL(blob);
  recordedBlobUrl.value = url;

  videoMuted.value = false;
  videoElement.value.srcObject = null;
  videoElement.value.src = url;
  videoElement.value.pause();
};

const startCamera = async () => {

    // If we somehow still have tracks, stop them before re-requesting
    stopStream();

  cameraError.value = null;

  try {
    const constraints = {
      audio: true,
      video: true,
    };
    const stream = await navigator.mediaDevices.getUserMedia(constraints);
    videoStream.value = stream;

    if (videoElement.value) {
      videoElement.value.srcObject = stream;
      videoElement.value.play().catch(() => {});
    }

    videoMuted.value = true;
  } catch (error) {
    console.error('[CreatorChatVideoMemoRecorder] getUserMedia error:', error);
    cameraError.value =
      error && error.message
        ? error.message
        : 'Unable to access camera/mic.';
  }
};

const internalStartRecording = () => {
  recordedChunks.value = [];

  if (recordedBlobUrl.value) {
    try {
      URL.revokeObjectURL(recordedBlobUrl.value);
    } catch (_) {
        // nothing
    }
    recordedBlobUrl.value = null;
  }

  try {
    mediaRecorder = new MediaRecorder(videoStream.value, {
      mimeType: 'video/webm;codecs=vp9',
    });
  } catch (error) {
    try {
      mediaRecorder = new MediaRecorder(videoStream.value);
    } catch (err) {
      console.error(
        '[CreatorChatVideoMemoRecorder] MediaRecorder is not supported:',
        err,
      );
      return;
    }
  }

  mediaRecorder.ondataavailable = (event) => {
    if (event.data && event.data.size > 0) {
      recordedChunks.value.push(event.data);
    }
  };

  mediaRecorder.onerror = (e) => {
    console.error('[CreatorChatVideoMemoRecorder] MediaRecorder error:', e);
  };

  mediaRecorder.onstop = () => {
    clearRecordingTimers();
    isRecording.value = false;
    createPreviewFromChunks();
    stopStream();
  };

  mediaRecorder.start();
  isRecording.value = true;
  // reset + start countdown
recordingSeconds.value = 0;
clearRecordingTimers();

// tick every second
recordingInterval = setInterval(() => {
  recordingSeconds.value += 1;

  // safety: if we ever overshoot, stop
  if (recordingSeconds.value >= MAX_RECORDING_SECONDS) {
    stopRecording();
  }
}, 1000);

// hard cap (authoritative stop)
recordingAutoStopTimeout = setTimeout(() => {
  stopRecording();
}, MAX_RECORDING_SECONDS * 1000);


};

const startRecording = async () => {
  if (!videoStream.value) {
    await startCamera();
    if (!videoStream.value) return;
  }
  internalStartRecording();
};

const stopRecording = () => {
    clearRecordingTimers();
  if (mediaRecorder && mediaRecorder.state !== 'inactive') {
    mediaRecorder.stop();
  } else {
    isRecording.value = false;
  }
};

const resetRecording = () => {
    clearRecordingTimers();
    recordingSeconds.value = 0;

  recordedChunks.value = [];

  if (recordedBlobUrl.value) {
    try {
      URL.revokeObjectURL(recordedBlobUrl.value);
    } catch (_) {
        // nothing
    }
    recordedBlobUrl.value = null;
  }

  if (videoElement.value) {
    try {
      videoElement.value.pause();
    } catch (_) {
        // nothing
    }
    videoElement.value.removeAttribute('src');
    videoElement.value.srcObject = null;
  }
};

const startOver = async () => {
  // Clear the recorded preview/chunks
  resetRecording();

  // Clear any prior camera error
  cameraError.value = null;

  // Ensure live preview mode
  videoMuted.value = true;

  // Re-start camera so user immediately returns to "Start Recording" state
  await startCamera();
};


const playRecordedMessage = () => {
  if (!hasRecording.value || !videoElement.value) return;

  if (recordedBlobUrl.value) {
    videoMuted.value = false;
    videoElement.value.srcObject = null;
    videoElement.value.src = recordedBlobUrl.value;
    videoElement.value
      .play()
      .catch((err) => {
        console.warn('[CreatorChatVideoMemoRecorder] play() failed:', err);
      });
    return;
  }

  const blob = new Blob(recordedChunks.value, { type: 'video/webm' });
  const url = URL.createObjectURL(blob);
  recordedBlobUrl.value = url;

  videoMuted.value = false;
  videoElement.value.srcObject = null;
  videoElement.value.src = url;
  videoElement.value
    .play()
    .catch((err) => {
      console.warn('[CreatorChatVideoMemoRecorder] play() failed:', err);
    });
};

const stopStream = () => {
  if (videoStream.value instanceof MediaStream) {
    videoStream.value.getTracks().forEach((track) => track.stop());
  }
  videoStream.value = null;
};

const sendToChat = async () => {
  if (!hasRecording.value || isUploading.value) return;

  isUploading.value = true;
  try {
    const blob = new Blob(recordedChunks.value, { type: 'video/webm' });
    const filename = `chat-video-${Date.now()}.webm`;
    const file = new File([blob], filename, {
      type: blob.type || 'video/webm',
    });

    const uploadedMeta = await props.uploadChatVideoFn(file);
    if (!uploadedMeta) {
      console.warn(
        '[CreatorChatVideoMemoRecorder] uploadChatVideoFn returned no meta',
      );
      return;
    }

    emit('send', uploadedMeta);

    resetRecording();
    stopStream();
    emit('close');
  } catch (err) {
    console.error(
      '[CreatorChatVideoMemoRecorder] Failed to upload/send video memo',
      err,
    );
  } finally {
    isUploading.value = false;
  }
};

const handleClose = () => {
  if (isRecording.value && mediaRecorder && mediaRecorder.state !== 'inactive') {
    mediaRecorder.stop();
  }
  isRecording.value = false;
  resetRecording();
  stopStream();
  clearRecordingTimers();
  recordingSeconds.value = 0;

  emit('close');
};

watch(
  () => props.show,
  (visible) => {
    if (!visible) {
      if (isRecording.value && mediaRecorder && mediaRecorder.state !== 'inactive') {
        mediaRecorder.stop();
      }
      isRecording.value = false;
      resetRecording();
      stopStream();
      clearRecordingTimers();
      recordingSeconds.value = 0;

    } else {
      resetRecording();
      cameraError.value = null;
      startCamera();
    }
  },
);

onBeforeUnmount(() => {
  if (isRecording.value && mediaRecorder && mediaRecorder.state !== 'inactive') {
    mediaRecorder.stop();
  }
  resetRecording();
  stopStream();
  clearRecordingTimers();
});
</script>



